import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const LINES = ["Bespoke", "Culinary", "Journeys"];

export default function Hero() {
  const paneRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: paneRef,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  return (
    <section
      data-testid="hero-section"
      className="relative grid lg:grid-cols-2 min-h-screen border-b border-white/10"
    >
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 pt-36 pb-20 lg:py-0">
        <motion.p
          data-testid="hero-overline"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.15 }}
          className="text-[11px] uppercase tracking-[0.4em] text-[#EAE6DF]/70 mb-10"
        >
          Private Chef — New York
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight text-white"
        >
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.9 }}
          className="mt-10 max-w-md text-sm sm:text-base font-light leading-relaxed text-[#F5F5F7]/70 tracking-wide"
        >
          Exclusive in-home dining for New York City's discerning families.
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.15 }}
          className="mt-16 flex items-center gap-6"
        >
          <span className="h-px w-16 bg-white/20" />
          <span className="text-[11px] uppercase tracking-[0.3em] text-white/40">
            Manhattan · Brooklyn · The Hamptons
          </span>
        </motion.div>
      </div>

      <div
        ref={paneRef}
        className="relative overflow-hidden h-[70vh] lg:h-screen border-t lg:border-t-0 lg:border-l border-white/10"
      >
        <motion.div style={{ y }} className="absolute inset-0">
          <motion.img
            data-testid="hero-image"
            src="/images/hero.png"
            alt="Chef finishing an exquisite minimalist plated dish"
            initial={{ scale: 1.12, opacity: 0.5 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
            className="h-full w-full object-cover"
          />
        </motion.div>
      </div>
    </section>
  );
}
